import * as THREE from 'three';
import { filesState } from './state/files.svelte';
function attachNormalsVisualizer(host, spatialData) {
    const normalsVisualizer = host.createNormalsVisualizer(spatialData);
    // Set initial visibility based on stored state (default true)
    const fileIndex = spatialData.fileIndex || host.spatialFiles.length - 1;
    const initialVisible = host.normalsVisible[fileIndex] !== false;
    normalsVisualizer.visible = initialVisible;
    host.scene.add(normalsVisualizer);
    // Ensure the array has the correct size and place the visualizer at the right index
    while (host.normalsVisualizers.length <= fileIndex) {
        host.normalsVisualizers.push(null);
    }
    host.normalsVisualizers[fileIndex] = normalsVisualizer;
}
export async function handlePcdData(host, message) {
    try {
        console.log(`Load: recv PCD ${message.fileName}`);
        host.showStatus(`PCD: processing ${message.fileName}`);
        const pcdData = message.data;
        console.log(`PCD: ${pcdData.vertexCount} points, format=${pcdData.format}, colors=${pcdData.hasColors}, normals=${pcdData.hasNormals}, intensity=${pcdData.hasIntensity}`);
        // Convert PCD data to PLY format for rendering
        const spatialData = {
            vertices: [],
            faces: [],
            format: pcdData.format === 'binary' ? 'binary_little_endian' : 'ascii',
            version: '1.0',
            comments: [
                `Converted from PCD: ${message.fileName}`,
                `Original format: ${pcdData.format}`,
                `Width: ${pcdData.width}, Height: ${pcdData.height}`,
                `Fields: ${pcdData.fields?.join(', ') || 'unknown'}`,
                ...pcdData.comments,
            ],
            sourcePointCount: pcdData.sourcePointCount,
            sourcePointIndices: pcdData.sourcePointIndices,
            vertexCount: pcdData.vertexCount,
            faceCount: 0,
            hasColors: pcdData.hasColors,
            hasNormals: pcdData.hasNormals,
            hasIntensity: pcdData.hasIntensity,
            fileName: message.fileName,
            shortPath: message.shortPath,
            fileSizeInBytes: message.fileSizeInBytes,
        };
        spatialData.useTypedArrays = true;
        spatialData.positionsArray = pcdData.positionsArray;
        spatialData.colorsArray = pcdData.colorsArray;
        spatialData.normalsArray = pcdData.normalsArray;
        spatialData.intensityArray = pcdData.intensityArray;
        spatialData.scalarFields = pcdData.scalarFields ?? {};
        // Carry the PCD viewpoint so we can set the initial transform after the
        // file is registered (at which point we know the fileIndex).
        const vp = pcdData.viewpoint ?? [0, 0, 0, 1, 0, 0, 0];
        const isIdentityViewpoint = vp[0] === 0 &&
            vp[1] === 0 &&
            vp[2] === 0 &&
            vp[3] === 1 &&
            vp[4] === 0 &&
            vp[5] === 0 &&
            vp[6] === 0;
        if (message.isAddFile) {
            host.addNewFiles([spatialData]);
        }
        else {
            await host.displayFiles([spatialData]);
        }
        // Apply PCD VIEWPOINT as the initial object transform (skip identity — it's the default).
        // The point coordinates are stored as-is from the file (no axis conversion), so the
        // viewpoint quaternion/translation is applied in the same PCL coordinate space.
        // The user's OpenCV/OpenGL convention toggle handles the overall viewing perspective
        // on top of this, just as it does for all other PCD data.
        if (!isIdentityViewpoint) {
            const fileIndex = spatialData.fileIndex ?? host.spatialFiles.length - 1;
            if (fileIndex >= 0 && fileIndex < host.transformationMatrices.length) {
                // PCD viewpoint: tx ty tz  qw qx qy qz
                const [tx, ty, tz, qw, qx, qy, qz] = vp;
                const q = new THREE.Quaternion(qx, qy, qz, qw).normalize();
                const viewpointMatrix = new THREE.Matrix4();
                viewpointMatrix.makeRotationFromQuaternion(q);
                viewpointMatrix.setPosition(tx, ty, tz);
                host.setTransformationMatrix(fileIndex, viewpointMatrix);
            }
        }
        // Create normals visualizer if PCD has normals
        if (spatialData.hasNormals) {
            attachNormalsVisualizer(host, spatialData);
        }
        host.showStatus(`PCD: loaded ${pcdData.vertexCount} points from ${message.fileName}`);
    }
    catch (error) {
        console.error('Error handling PCD data:', error);
        host.showError(`PCD processing failed: ${error instanceof Error ? error.message : String(error)}`);
    }
}
export async function handleNpyData(host, message) {
    try {
        console.log(`Load: recv NPY point cloud ${message.fileName}`);
        host.showStatus(`NPY: processing point cloud data from ${message.fileName}`);
        const npyData = message.data;
        console.log(`NPY: ${npyData.vertexCount} points, format=${npyData.format}, colors=${npyData.hasColors}, normals=${npyData.hasNormals}`);
        // NPY data is already in PLY format from the parser
        const spatialData = {
            ...npyData,
            fileName: message.fileName,
            shortPath: message.shortPath,
        };
        if (message.isAddFile) {
            spatialData.fileIndex = host.spatialFiles.length;
        }
        await host.displayFiles([spatialData]);
        // Handle normals visualization if available
        const fileIndex = spatialData.fileIndex;
        if (npyData.hasNormals) {
            // Ensure normalsVisualizers array is properly sized
            while (host.normalsVisualizers.length <= fileIndex) {
                host.normalsVisualizers.push(null);
            }
            const normalsVisualizer = host.createNormalsVisualizer(spatialData);
            if (normalsVisualizer) {
                host.scene.add(normalsVisualizer);
            }
            host.normalsVisualizers[fileIndex] = normalsVisualizer;
        }
        else {
            // Ensure array is properly sized even without normals
            while (host.normalsVisualizers.length <= fileIndex) {
                host.normalsVisualizers.push(null);
            }
            host.normalsVisualizers[fileIndex] = null;
        }
        host.showStatus(`NPY: loaded ${npyData.vertexCount} points from ${message.fileName}`);
    }
    catch (error) {
        console.error('Error handling NPY data:', error);
        host.showError(`NPY processing failed: ${error instanceof Error ? error.message : String(error)}`);
    }
}
export async function handlePtsData(host, message) {
    try {
        console.log(`Load: recv PTS ${message.fileName}`);
        host.showStatus(`PTS: processing ${message.fileName}`);
        const ptsData = message.data;
        console.log(`PTS: ${ptsData.vertexCount} points, format=${ptsData.detectedFormat}, colors=${ptsData.hasColors}, normals=${ptsData.hasNormals}, intensity=${ptsData.hasIntensity}`);
        // Convert PTS data to PLY format for rendering
        const spatialData = {
            vertices: [],
            faces: [],
            format: 'ascii',
            version: '1.0',
            comments: [
                `Converted from PTS: ${message.fileName}`,
                `Detected format: ${ptsData.detectedFormat}`,
                ...ptsData.comments,
            ],
            vertexCount: ptsData.vertexCount,
            faceCount: 0,
            hasColors: ptsData.hasColors,
            hasNormals: ptsData.hasNormals,
            hasIntensity: ptsData.hasIntensity,
            fileName: message.fileName,
            shortPath: message.shortPath,
            fileSizeInBytes: message.fileSizeInBytes,
        };
        spatialData.useTypedArrays = true;
        spatialData.positionsArray = ptsData.positionsArray;
        spatialData.colorsArray = ptsData.colorsArray;
        spatialData.normalsArray = ptsData.normalsArray;
        spatialData.intensityArray = ptsData.intensityArray;
        spatialData.scalarFields = ptsData.scalarFields ?? {};
        if (message.isAddFile) {
            host.addNewFiles([spatialData]);
        }
        else {
            await host.displayFiles([spatialData]);
        }
        // Create normals visualizer if PTS has normals
        if (spatialData.hasNormals) {
            attachNormalsVisualizer(host, spatialData);
        }
        host.showStatus(`PTS: loaded ${ptsData.vertexCount} points from ${message.fileName}`);
    }
    catch (error) {
        console.error('Error handling PTS data:', error);
        host.showError(`PTS processing failed: ${error instanceof Error ? error.message : String(error)}`);
    }
}
export async function handleKittiBinData(host, message) {
    try {
        console.log(`Load: recv KITTI BIN ${message.fileName}`);
        host.showStatus(`KITTI BIN: processing ${message.fileName}`);
        const kittiData = message.data;
        console.log(`KITTI BIN: ${kittiData.vertexCount} points, format=${kittiData.detectedFormat}, intensity=${kittiData.hasIntensity}`);
        // Convert KITTI BIN data to the shared spatial format for rendering
        const spatialData = {
            vertices: [],
            faces: [],
            format: 'binary_little_endian',
            version: '1.0',
            comments: [
                `Converted from KITTI BIN: ${message.fileName}`,
                `Detected format: ${kittiData.detectedFormat}`,
                ...(kittiData.comments ?? []),
            ],
            vertexCount: kittiData.vertexCount,
            faceCount: 0,
            hasColors: kittiData.hasColors,
            hasNormals: kittiData.hasNormals,
            hasIntensity: kittiData.hasIntensity,
            fileName: message.fileName,
            shortPath: message.shortPath,
            fileSizeInBytes: message.fileSizeInBytes,
        };
        spatialData.useTypedArrays = true;
        spatialData.positionsArray = kittiData.positionsArray;
        spatialData.colorsArray = kittiData.colorsArray;
        spatialData.normalsArray = kittiData.normalsArray;
        spatialData.intensityArray = kittiData.intensityArray;
        spatialData.scalarFields = kittiData.scalarFields ?? {};
        if (message.isAddFile) {
            host.addNewFiles([spatialData]);
        }
        else {
            await host.displayFiles([spatialData]);
        }
        host.showStatus(`KITTI BIN: loaded ${kittiData.vertexCount} points from ${message.fileName}`);
    }
    catch (error) {
        console.error('Error handling KITTI BIN data:', error);
        host.showError(`KITTI BIN processing failed: ${error instanceof Error ? error.message : String(error)}`);
    }
}
export async function handleOffData(host, message) {
    try {
        console.log(`Load: recv OFF ${message.fileName}`);
        host.showStatus(`OFF: processing ${message.fileName}`);
        const offData = message.data;
        console.log(`OFF: ${offData.vertexCount} vertices, ${offData.faceCount} faces, variant=${offData.offVariant}, colors=${offData.hasColors}, normals=${offData.hasNormals}`);
        // Convert OFF data to PLY format for rendering
        const spatialData = {
            vertices: offData.vertices,
            faces: offData.faces,
            format: 'ascii',
            version: '1.0',
            comments: [
                `Converted from OFF: ${message.fileName}`,
                `OFF variant: ${offData.offVariant}`,
                ...offData.comments,
            ],
            vertexCount: offData.vertexCount,
            faceCount: offData.faceCount,
            hasColors: offData.hasColors,
            hasNormals: offData.hasNormals,
            fileName: message.fileName,
            shortPath: message.shortPath,
            fileSizeInBytes: message.fileSizeInBytes,
        };
        if (message.isAddFile) {
            host.addNewFiles([spatialData]);
        }
        else {
            await host.displayFiles([spatialData]);
        }
        // Create normals visualizer if OFF has normals (for both meshes and point clouds)
        if (spatialData.hasNormals) {
            attachNormalsVisualizer(host, spatialData);
        }
        const meshType = offData.faceCount > 0 ? 'mesh' : 'point cloud';
        host.showStatus(`OFF: loaded ${offData.vertexCount} vertices, ${offData.faceCount} faces as ${meshType} from ${message.fileName}`);
    }
    catch (error) {
        console.error('Error handling OFF data:', error);
        host.showError(`OFF processing failed: ${error instanceof Error ? error.message : String(error)}`);
    }
}
/**
 * An isosurface extracted from a volume (NRRD, or a stack handed over by
 * tiff-visualizer).
 *
 * The extension host has already done the marching-cubes pass, so what arrives
 * is finished `SpatialData` on the typed-array path rather than a
 * format-specific shape needing conversion — which is exactly the point of
 * routing volumes through the mesh pipeline instead of building a second
 * renderer.
 */
export async function handleVolumeData(host, message) {
    try {
        console.log(`Load: recv volume ${message.fileName}`);
        host.showStatus(`Volume: processing ${message.fileName}`);
        const replacing = Number.isInteger(message.replaceFileIndex);
        const oldData = replacing ? host.spatialFiles[message.replaceFileIndex] : undefined;
        const spatialData = {
            ...message.data,
            fileName: message.fileName,
            shortPath: message.shortPath ?? oldData?.shortPath,
            fileSizeInBytes: message.fileSizeInBytes ?? oldData?.fileSizeInBytes,
        };
        if (replacing) {
            replaceVolumeGeometry(host, message.replaceFileIndex, spatialData);
        }
        else if (message.isAddFile) {
            host.addNewFiles([spatialData]);
        }
        else {
            await host.displayFiles([spatialData]);
        }
        const threshold = spatialData.metadata?.threshold;
        const units = spatialData.metadata?.intensityUnits;
        const renderMode = spatialData.metadata?.volumeRenderMode;
        const at = renderMode === 'slices' || threshold === undefined
            ? ''
            : ` at ${Math.round(threshold)}${units ? ` ${units}` : ''}`;
        const summary = renderMode === 'points'
            ? `${spatialData.vertexCount.toLocaleString()} points`
            : renderMode === 'slices'
                ? `orthogonal slices at ${spatialData.metadata?.sliceIndices?.join(' / ') || 'centre'}`
                : renderMode === 'voxels'
                    ? `${Number(spatialData.metadata?.renderedVoxelCount ?? 0).toLocaleString()} voxels, ${spatialData.faceCount.toLocaleString()} triangles`
                    : `${spatialData.vertexCount.toLocaleString()} vertices, ${spatialData.faceCount.toLocaleString()} triangles`;
        const modeLabel = renderMode === 'points'
            ? 'point cloud'
            : renderMode === 'slices'
                ? 'slices'
                : renderMode === 'voxels'
                    ? 'voxels'
                    : 'mesh';
        host.showStatus(`Volume: ${modeLabel}${at} — ${summary} from ${message.fileName}`);
    }
    catch (error) {
        console.error('Error handling volume data:', error);
        host.showError(`Volume processing failed: ${error instanceof Error ? error.message : String(error)}`);
    }
}
function replaceVolumeGeometry(host, fileIndex, data) {
    if (fileIndex < 0 || fileIndex >= host.spatialFiles.length) {
        throw new Error(`Cannot replace missing volume at file index ${fileIndex}`);
    }
    const previous = host.meshes[fileIndex];
    const previousRenderMode = host.spatialFiles[fileIndex]?.metadata?.volumeRenderMode;
    const matrix = host.transformationMatrices[fileIndex].clone();
    const visible = host.fileVisibility[fileIndex] ?? true;
    if (previous) {
        host.scene.remove(previous);
        previous.geometry.dispose();
        const materials = Array.isArray(previous.material) ? previous.material : [previous.material];
        materials.forEach(material => material.dispose());
    }
    const vertexPoints = host.vertexPointsObjects[fileIndex];
    if (vertexPoints) {
        host.scene.remove(vertexPoints);
        vertexPoints.geometry.dispose();
        const materials = Array.isArray(vertexPoints.material)
            ? vertexPoints.material
            : [vertexPoints.material];
        materials.forEach(material => material.dispose());
        host.vertexPointsObjects[fileIndex] = null;
    }
    const normals = host.normalsVisualizers[fileIndex];
    if (normals) {
        host.scene.remove(normals);
        normals.geometry.dispose();
        const materials = Array.isArray(normals.material) ? normals.material : [normals.material];
        materials.forEach(material => material.dispose());
        host.normalsVisualizers[fileIndex] = null;
    }
    data.fileIndex = fileIndex;
    host.spatialFiles[fileIndex] = data;
    if (data.metadata?.volumeRenderMode === 'points' && previousRenderMode !== 'points') {
        host.individualColorModes[fileIndex] = 'original';
        filesState.colorModes[fileIndex] = 'original';
    }
    else if (data.metadata?.volumeRenderMode === 'slices' && previousRenderMode !== 'slices') {
        host.individualColorModes[fileIndex] = 'original';
        filesState.colorModes[fileIndex] = 'original';
    }
    else if (data.metadata?.volumeRenderMode === 'voxels' && previousRenderMode !== 'voxels') {
        host.individualColorModes[fileIndex] = 'original';
        filesState.colorModes[fileIndex] = 'original';
    }
    else if (data.metadata?.volumeRenderMode === 'mesh' && previousRenderMode !== 'mesh') {
        host.individualColorModes[fileIndex] = 'assigned';
        filesState.colorModes[fileIndex] = 'assigned';
    }
    const geometry = host.createGeometryFromSpatialData(data);
    const material = host.createMaterialForFile(data, fileIndex);
    const object = data.faceCount > 0
        ? new THREE.Mesh(geometry, material)
        : new THREE.Points(geometry, material);
    object.visible = visible;
    host.meshes[fileIndex] = object;
    host.scene.add(object);
    host.solidVisible[fileIndex] = data.faceCount > 0;
    host.pointsVisible[fileIndex] = data.faceCount === 0;
    host.wireframeVisible[fileIndex] = false;
    if (data.hasNormals && host.normalsVisible[fileIndex]) {
        const visualizer = host.createNormalsVisualizer(data);
        visualizer.visible = visible;
        host.normalsVisualizers[fileIndex] = visualizer;
        host.scene.add(visualizer);
    }
    else if (!data.hasNormals) {
        host.normalsVisible[fileIndex] = false;
    }
    host.setTransformationMatrix(fileIndex, matrix);
    host.updateFileList();
    host.updateFileStats();
    host.requestRender();
}
export async function handleGltfData(host, message) {
    try {
        console.log(`Load: recv GLTF/GLB ${message.fileName}`);
        host.showStatus(`GLTF: processing ${message.fileName}`);
        const gltfData = message.data;
        console.log(`GLTF: ${gltfData.vertexCount} vertices, ${gltfData.faceCount} faces, ${gltfData.meshCount} meshes, ${gltfData.materialCount} materials, colors=${gltfData.hasColors}, normals=${gltfData.hasNormals}`);
        // Convert GLTF data to PLY format for rendering
        const spatialData = {
            vertices: gltfData.vertices,
            faces: gltfData.faces,
            format: 'ascii',
            version: '1.0',
            comments: [
                `Converted from GLTF/GLB: ${message.fileName}`,
                `Format: ${gltfData.format}`,
                `Meshes: ${gltfData.meshCount}, Materials: ${gltfData.materialCount}`,
                ...gltfData.comments,
            ],
            vertexCount: gltfData.vertexCount,
            faceCount: gltfData.faceCount,
            hasColors: gltfData.hasColors,
            hasNormals: gltfData.hasNormals,
            fileName: message.fileName,
            shortPath: message.shortPath,
            fileSizeInBytes: message.fileSizeInBytes,
        };
        if (message.isAddFile) {
            host.addNewFiles([spatialData]);
        }
        else {
            await host.displayFiles([spatialData]);
        }
        const meshType = gltfData.faceCount > 0 ? 'mesh' : 'point cloud';
        host.showStatus(`GLTF: loaded ${gltfData.vertexCount} vertices, ${gltfData.faceCount} faces as ${meshType} from ${message.fileName}`);
    }
    catch (error) {
        console.error('Error handling GLTF data:', error);
        host.showError(`GLTF processing failed: ${error instanceof Error ? error.message : String(error)}`);
    }
}
export async function handleXyzVariantData(host, message) {
    try {
        console.log(`Load: recv XYZ variant (${message.variant}) ${message.fileName}`);
        host.showStatus(`XYZ: processing ${message.fileName} (${message.variant})`);
        // Data is already parsed into typed arrays by the extension (XyzVariantParser).
        const xyzData = message.data;
        const spatialData = {
            vertices: [],
            faces: [],
            format: 'ascii',
            version: '1.0',
            comments: [
                `Converted from ${message.variant.toUpperCase()}: ${message.fileName}`,
                `Format variant: ${message.variant}`,
            ],
            vertexCount: xyzData.vertexCount,
            faceCount: 0,
            hasColors: xyzData.hasColors,
            hasNormals: xyzData.hasNormals,
            hasIntensity: xyzData.hasIntensity,
            fileName: message.fileName,
            shortPath: message.shortPath,
            fileSizeInBytes: message.fileSizeInBytes,
        };
        spatialData.useTypedArrays = true;
        spatialData.positionsArray = xyzData.positionsArray;
        spatialData.colorsArray = xyzData.colorsArray;
        spatialData.normalsArray = xyzData.normalsArray;
        spatialData.intensityArray = xyzData.intensityArray;
        spatialData.scalarFields = xyzData.intensityArray
            ? { intensity: xyzData.intensityArray }
            : {};
        if (message.isAddFile) {
            host.addNewFiles([spatialData]);
        }
        else {
            await host.displayFiles([spatialData]);
        }
        if (spatialData.hasNormals) {
            attachNormalsVisualizer(host, spatialData);
        }
        host.showStatus(`${message.variant.toUpperCase()}: loaded ${spatialData.vertexCount} points from ${message.fileName}`);
    }
    catch (error) {
        console.error('Error handling XYZ variant data:', error);
        host.showError(`${message.variant.toUpperCase()} processing failed: ${error instanceof Error ? error.message : String(error)}`);
    }
}
export async function handleObjData(host, message) {
    try {
        console.log(`Load: recv OBJ ${message.fileName}`);
        host.showStatus(`OBJ: processing ${message.fileName}`);
        const objData = message.data;
        const hasFaces = objData.faceCount > 0;
        const hasLines = objData.lineCount > 0;
        const hasPoints = objData.pointCount > 0;
        console.log(`OBJ: v=${objData.vertexCount}, pts=${objData.pointCount}, f=${objData.faceCount}, lines=${objData.lineCount}, groups=${objData.materialGroups ? objData.materialGroups.length : 0}`);
        // Convert OBJ vertices to PLY format
        const vertices = objData.vertices.map((v) => ({
            x: v.x,
            y: v.y,
            z: v.z,
            red: 128, // Default gray color
            green: 128,
            blue: 128,
        }));
        // Convert OBJ faces to PLY format if they exist
        const faces = [];
        if (hasFaces) {
            for (const objFace of objData.faces) {
                if (objFace.indices.length >= 3) {
                    faces.push({
                        indices: objFace.indices,
                    });
                }
            }
        }
        // Create PLY data structure
        const spatialData = {
            vertices,
            faces,
            format: 'ascii',
            version: '1.0',
            comments: [`Converted from OBJ file: ${message.fileName}`],
            vertexCount: vertices.length,
            faceCount: faces.length,
            hasColors: true,
            hasNormals: objData.hasNormals,
            fileName: message.fileName, // Keep original OBJ filename
            shortPath: message.shortPath,
            fileIndex: host.spatialFiles.length,
            fileSizeInBytes: message.fileSizeInBytes,
        };
        // Store OBJ-specific data for enhanced rendering
        spatialData.objData = objData;
        spatialData.isObjFile = true;
        spatialData.objRenderType = hasFaces ? 'mesh' : 'wireframe';
        // Store line data for wireframe rendering (either as primary or secondary visualization)
        if (hasLines) {
            spatialData.objLines = objData.lines;
            spatialData.hasWireframe = true;
        }
        // Store point data for point rendering
        if (hasPoints) {
            spatialData.objPoints = objData.points;
            spatialData.hasPoints = true;
        }
        // Add to visualization
        if (message.isAddFile) {
            host.addNewFiles([spatialData]);
        }
        else {
            await host.displayFiles([spatialData]);
        }
        // Status message based on what was loaded
        const statusParts = [`${vertices.length.toLocaleString()} vertices`];
        if (hasPoints) {
            statusParts.push(`${objData.pointCount} points`);
        }
        if (hasFaces) {
            statusParts.push(`${faces.length.toLocaleString()} faces`);
        }
        if (hasLines) {
            statusParts.push(`${objData.lineCount} line segments`);
        }
        if (objData.hasTextures) {
            statusParts.push(`${objData.textureCoordCount} texture coords`);
        }
        if (objData.hasNormals) {
            statusParts.push(`${objData.normalCount} normals`);
        }
        host.showStatus(`OBJ ${hasFaces ? 'mesh' : 'wireframe'} loaded: ${statusParts.join(', ')}`);
    }
    catch (error) {
        console.error('Error handling OBJ data:', error);
        host.showError(`Failed to process OBJ file: ${error instanceof Error ? error.message : String(error)}`);
    }
}
export async function handleStlData(host, message) {
    try {
        console.log(`Load: recv STL ${message.fileName}`);
        host.showStatus(`STL: processing ${message.fileName}`);
        const stlData = message.data;
        const hasColors = stlData.hasColors;
        console.log(`STL: ${stlData.triangleCount} triangles, format=${stlData.format}, colors=${hasColors}`);
        // Handle empty STL files
        if (stlData.triangleCount === 0 || !stlData.triangles || stlData.triangles.length === 0) {
            console.log('STL: Empty mesh detected');
            host.showStatus(`STL: Empty mesh loaded (${message.fileName})`);
            // Create minimal PLY data for empty mesh
            const spatialData = {
                vertices: [],
                faces: [],
                format: stlData.format === 'binary' ? 'binary_little_endian' : 'ascii',
                version: '1.0',
                comments: [
                    `Empty STL mesh: ${message.fileName}`,
                    `Original format: ${stlData.format}`,
                    ...(stlData.header ? [`Header: ${stlData.header}`] : []),
                ],
                vertexCount: 0,
                faceCount: 0,
                hasColors: false,
                hasNormals: false,
                fileName: message.fileName.replace(/\.stl$/i, '_empty.ply'),
                shortPath: message.shortPath,
                fileIndex: host.spatialFiles.length,
                fileSizeInBytes: message.fileSizeInBytes,
            };
            // Add to visualization (even empty files should be tracked)
            if (message.isAddFile) {
                host.addNewFiles([spatialData]);
            }
            else {
                await host.displayFiles([spatialData]);
            }
            return;
        }
        // Convert STL triangles to PLY vertices and faces
        const vertices = [];
        const faces = [];
        const vertexMap = new Map(); // For deduplication
        let vertexIndex = 0;
        for (let i = 0; i < stlData.triangles.length; i++) {
            const triangle = stlData.triangles[i];
            const faceIndices = [];
            // Process each vertex of the triangle
            for (let j = 0; j < 3; j++) {
                const vertex = triangle.vertices[j];
                const key = `${vertex.x},${vertex.y},${vertex.z}`;
                let vIndex = vertexMap.get(key);
                if (vIndex === undefined) {
                    // New vertex
                    vIndex = vertexIndex++;
                    vertexMap.set(key, vIndex);
                    const plyVertex = {
                        x: vertex.x,
                        y: vertex.y,
                        z: vertex.z,
                        nx: triangle.normal.x,
                        ny: triangle.normal.y,
                        nz: triangle.normal.z,
                    };
                    // Add color if available
                    if (hasColors && triangle.color) {
                        plyVertex.red = triangle.color.red;
                        plyVertex.green = triangle.color.green;
                        plyVertex.blue = triangle.color.blue;
                    }
                    else {
                        // Default gray color
                        plyVertex.red = 180;
                        plyVertex.green = 180;
                        plyVertex.blue = 180;
                    }
                    vertices.push(plyVertex);
                }
                faceIndices.push(vIndex);
            }
            // Add the face
            faces.push({
                indices: faceIndices,
            });
        }
        // Create PLY data structure
        const spatialData = {
            vertices,
            faces,
            format: stlData.format === 'binary' ? 'binary_little_endian' : 'ascii',
            version: '1.0',
            comments: [
                `Converted from STL file: ${message.fileName}`,
                `Original format: ${stlData.format}`,
                `Triangle count: ${stlData.triangleCount}`,
                ...(stlData.header ? [`Header: ${stlData.header}`] : []),
            ],
            vertexCount: vertices.length,
            faceCount: faces.length,
            hasColors: true,
            hasNormals: true,
            fileName: message.fileName.replace(/\.stl$/i, '_mesh.ply'),
            shortPath: message.shortPath,
            fileIndex: host.spatialFiles.length,
            fileSizeInBytes: message.fileSizeInBytes,
        };
        // Store STL-specific data for enhanced rendering
        spatialData.stlData = stlData;
        spatialData.isStlFile = true;
        spatialData.stlFormat = stlData.format;
        spatialData.stlTriangleCount = stlData.triangleCount;
        // Add to visualization
        if (message.isAddFile) {
            host.addNewFiles([spatialData]);
        }
        else {
            await host.displayFiles([spatialData]);
        }
        // Status message
        const statusParts = [
            `${vertices.length.toLocaleString()} vertices`,
            `${faces.length.toLocaleString()} triangles`,
            `${stlData.format} format`,
        ];
        if (hasColors) {
            statusParts.push('with colors');
        }
        host.showStatus(`STL mesh loaded: ${statusParts.join(', ')}`);
    }
    catch (error) {
        console.error('Error handling STL data:', error);
        host.showError(`Failed to process STL file: ${error instanceof Error ? error.message : String(error)}`);
    }
}
export async function handleXyzData(host, message) {
    try {
        console.log('Received XYZ data for processing:', message.fileName);
        host.showStatus('Parsing XYZ file...');
        // Parse XYZ file (simple format: x y z [r g b] per line)
        const decoder = new TextDecoder('utf-8');
        const text = decoder.decode(message.data);
        const lines = text.split('\n').filter(line => line.trim().length > 0);
        const vertices = [];
        let hasColors = false;
        for (const line of lines) {
            const parts = line.trim().split(/\s+/);
            if (parts.length >= 3) {
                const x = parseFloat(parts[0]);
                const y = parseFloat(parts[1]);
                const z = parseFloat(parts[2]);
                if (!isNaN(x) && !isNaN(y) && !isNaN(z)) {
                    const vertex = { x, y, z };
                    // Check for color data (RGB values)
                    if (parts.length >= 6) {
                        const r = parseInt(parts[3]);
                        const g = parseInt(parts[4]);
                        const b = parseInt(parts[5]);
                        if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
                            vertex.red = Math.max(0, Math.min(255, r));
                            vertex.green = Math.max(0, Math.min(255, g));
                            vertex.blue = Math.max(0, Math.min(255, b));
                            hasColors = true;
                        }
                    }
                    vertices.push(vertex);
                }
            }
        }
        if (vertices.length === 0) {
            throw new Error('No valid vertices found in XYZ file');
        }
        // Create PLY data structure
        const spatialData = {
            vertices,
            faces: [],
            format: 'ascii',
            version: '1.0',
            comments: [`Converted from XYZ file: ${message.fileName}`],
            vertexCount: vertices.length,
            faceCount: 0,
            hasColors,
            hasNormals: false,
            fileName: message.fileName.replace(/\.xyz$/i, '_pointcloud.ply'),
            shortPath: message.shortPath,
            fileIndex: host.spatialFiles.length,
        };
        // Add to visualization
        if (message.isAddFile) {
            host.addNewFiles([spatialData]);
        }
        else {
            await host.displayFiles([spatialData]);
        }
        host.showStatus(`XYZ file loaded successfully! ${vertices.length.toLocaleString()} points${hasColors ? ' with colors' : ''}`);
    }
    catch (error) {
        console.error('Error handling XYZ data:', error);
        host.showError(`Failed to process XYZ file: ${error instanceof Error ? error.message : String(error)}`);
    }
}
export function handleMtlData(host, message) {
    try {
        console.log('Received MTL data for file index:', message.fileIndex);
        const fileIndex = message.fileIndex;
        const mtlData = message.data;
        console.log('MTL data structure:', mtlData);
        console.log('Available materials:', Object.keys(mtlData.materials || {}));
        if (fileIndex < 0 || fileIndex >= host.spatialFiles.length) {
            console.error('Invalid file index for MTL data:', fileIndex);
            return;
        }
        const objFile = host.spatialFiles[fileIndex];
        const isObjFile = objFile.isObjFile || objFile.isObjWireframe;
        console.log('OBJ file data:', {
            isObjFile: objFile.isObjFile,
            isObjWireframe: objFile.isObjWireframe,
            objRenderType: objFile.objRenderType,
            fileName: objFile.fileName,
        });
        if (!isObjFile) {
            console.error('File is not an OBJ file:', fileIndex);
            return;
        }
        // Find the material to use - prioritize the current material from OBJ, then first material
        let materialColor = { r: 1.0, g: 0.0, b: 0.0 }; // Default red
        let materialName = '';
        if (mtlData.materials && Object.keys(mtlData.materials).length > 0) {
            const objData = objFile.objData;
            const materialNames = Object.keys(mtlData.materials);
            // Try to use the material referenced in the OBJ file first
            if (objData && objData.currentMaterial && mtlData.materials[objData.currentMaterial]) {
                const material = mtlData.materials[objData.currentMaterial];
                if (material.diffuseColor) {
                    materialColor = material.diffuseColor;
                    materialName = objData.currentMaterial;
                }
            }
            else {
                // Fall back to first available material
                const firstMaterial = mtlData.materials[materialNames[0]];
                if (firstMaterial && firstMaterial.diffuseColor) {
                    materialColor = firstMaterial.diffuseColor;
                    materialName = materialNames[0];
                }
            }
            console.log(`Using material '${materialName}' with color: RGB(${materialColor.r}, ${materialColor.g}, ${materialColor.b})`);
        }
        // Convert RGB 0-1 to Three.js hex color
        const hexColor = (Math.round(materialColor.r * 255) << 16) |
            (Math.round(materialColor.g * 255) << 8) |
            Math.round(materialColor.b * 255);
        // Update the mesh color based on current render type
        const mesh = host.meshes[fileIndex];
        const multiMaterialGroup = host.multiMaterialGroups[fileIndex];
        const subMeshes = host.materialMeshes[fileIndex];
        console.log('Mesh info:', {
            meshExists: !!mesh,
            meshType: mesh?.type,
            isLineSegments: mesh?.isLineSegments,
            isObjMesh: mesh?.isObjMesh,
            isMultiMaterial: mesh?.isMultiMaterial,
            multiMaterialGroupExists: !!multiMaterialGroup,
            subMeshCount: subMeshes?.length || 0,
            materialType: mesh?.material?.type,
        });
        if (multiMaterialGroup && subMeshes) {
            // Multi-material OBJ: apply materials to each sub-mesh
            let appliedCount = 0;
            for (const subMesh of subMeshes) {
                const subMaterialName = subMesh.materialName;
                if (subMaterialName && mtlData.materials[subMaterialName]) {
                    const subMaterial = mtlData.materials[subMaterialName];
                    if (subMaterial.diffuseColor) {
                        const subHexColor = (Math.round(subMaterial.diffuseColor.r * 255) << 16) |
                            (Math.round(subMaterial.diffuseColor.g * 255) << 8) |
                            Math.round(subMaterial.diffuseColor.b * 255);
                        const subMeshMaterial = subMesh.material;
                        if (subMeshMaterial && subMeshMaterial.color) {
                            subMeshMaterial.color.setHex(subHexColor);
                            console.log(`Applied ${subMaterialName} color #${subHexColor.toString(16).padStart(6, '0')} to sub-mesh`);
                            appliedCount++;
                        }
                    }
                }
            }
            console.log(`Applied materials to ${appliedCount}/${subMeshes.length} sub-meshes`);
            materialName = message.fileName; // For multi-material, show filename
        }
        else if (mesh && mesh.isLineSegments) {
            // Update wireframe color
            const lineMaterial = mesh.material;
            if (lineMaterial) {
                lineMaterial.color.setHex(hexColor);
                console.log(`Updated wireframe color to #${hexColor.toString(16).padStart(6, '0')}`);
            }
            materialName = message.fileName; // For single-material, show filename
        }
        else if (mesh && (mesh.isObjMesh || mesh.type === 'Mesh')) {
            // Update solid mesh color
            const meshMaterial = mesh.material;
            if (meshMaterial) {
                meshMaterial.color.setHex(hexColor);
                console.log(`Updated solid mesh color to #${hexColor.toString(16).padStart(6, '0')}`);
            }
            materialName = message.fileName; // For single-material, show filename
        }
        else if (mesh) {
            console.warn('Unknown mesh type, trying to update material anyway');
            const anyMaterial = mesh.material;
            if (anyMaterial && anyMaterial.color) {
                anyMaterial.color.setHex(hexColor);
                console.log(`Updated generic material color to #${hexColor.toString(16).padStart(6, '0')}`);
            }
            materialName = message.fileName; // For single-material, show filename
        }
        else {
            console.error('No mesh or multi-material group found at index:', fileIndex);
        }
        // Store the applied MTL color, name, and data for future use
        host.appliedMtlColors[fileIndex] = hexColor;
        host.appliedMtlNames[fileIndex] = materialName;
        host.appliedMtlData[fileIndex] = mtlData;
        // Update UI to show loaded MTL
        host.updateFileList();
        host.showStatus(`MTL material applied! Using material '${materialName}' from ${message.fileName}`);
    }
    catch (error) {
        console.error('Error handling MTL data:', error);
        host.showError(`Failed to apply MTL material: ${error instanceof Error ? error.message : String(error)}`);
    }
}
